import React from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { HourlyTariffWindow, PriceHistoryRow } from "../types";
import { CommunityPriceCardHeader } from "./card/CommunityPriceCardHeader";
import { CommunityPriceCardDuration } from "./card/CommunityPriceCardDuration";

export interface PriceHistoryCardProps {
  item: PriceHistoryRow;
}

const PriceLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-content-between align-items-center py-1">
    <span className="text-600 text-sm">{label}</span>
    <span className="font-semibold text-800">{value}</span>
  </div>
);

const FeeSection: React.FC<{ icon: string; iconColor: string; title: string; children: React.ReactNode }> = ({
  title, children,
}) => (
  <div className="flex flex-column gap-1">
    <div className="flex align-items-center gap-2 mb-1">
      <span className="text-xs font-semibold text-500 uppercase" style={{ letterSpacing: "0.05em" }}>{title}</span>
    </div>
    {children}
  </div>
);

// ─── Static tariff layout ─────────────────────────────────────────────────────

const StaticTariffBody: React.FC<{
  energyPrice: string;
  activationFee?: string;
  blockingFee: string;
  blockingFeeAfterHours: string;
  blockingFeeCap: string;
  blockingFeeCapAfterHours: string;
}> = ({ energyPrice, activationFee, blockingFee, blockingFeeAfterHours, blockingFeeCap, blockingFeeCapAfterHours }) => (
  <div className="flex flex-column gap-3">
    <FeeSection icon="pi-bolt" iconColor="#f59e0b" title="Energy">
      <PriceLine label="Price per kWh" value={energyPrice} />
      {activationFee && activationFee !== "—" && (
        <PriceLine label="Activation fee" value={activationFee} />
      )}
    </FeeSection>
    <Divider className="my-0" />
    <FeeSection icon="pi-stop-circle" iconColor="#ef4444" title="Blocking Fee">
      <PriceLine label="Rate per hour" value={blockingFee} />
      <PriceLine label="Starts after" value={blockingFeeAfterHours} />
      <PriceLine label="Cap" value={blockingFeeCap} />
      <PriceLine label="Cap applies after" value={blockingFeeCapAfterHours} />
    </FeeSection>
  </div>
);

// ─── Day / Night period block ─────────────────────────────────────────────────

const PeriodBlock: React.FC<{
  icon: string;
  iconColor: string;
  label: string;
  energyPrice?: string;
  blockingFee?: string;
  blockingFeeAfterHours?: string;
  blockingFeeCap?: string;
  blockingFeeCapAfterHours?: string;
}> = ({ icon, iconColor, label, energyPrice, blockingFee, blockingFeeAfterHours, blockingFeeCap, blockingFeeCapAfterHours }) => (
  <div className="flex flex-column gap-3 p-3 border-round" style={{ background: "var(--surface-50)" }}>
    <div className="flex align-items-center gap-2">
      <i className={`pi ${icon}`} style={{ color: iconColor }} />
      <span className="font-semibold text-700">{label}</span>
    </div>
    <FeeSection icon="pi-bolt" iconColor="#f59e0b" title="Energy">
      <PriceLine label="Price per kWh" value={energyPrice ?? "—"} />
    </FeeSection>
    <Divider className="my-0" />
    <FeeSection icon="pi-stop-circle" iconColor="#ef4444" title="Blocking Fee">
      <PriceLine label="Rate per hour" value={blockingFee ?? "—"} />
      <PriceLine label="Starts after" value={blockingFeeAfterHours ?? "—"} />
      <PriceLine label="Cap" value={blockingFeeCap ?? "—"} />
      <PriceLine label="Cap applies after" value={blockingFeeCapAfterHours ?? "—"} />
    </FeeSection>
  </div>
);

// ─── Hourly tariff ────────────────────────────────────────────────────────────

const WEEKDAY_SHORT: Record<string, string> = {
  MONDAY: "MON", TUESDAY: "TUE", WEDNESDAY: "WED", THURSDAY: "THU",
  FRIDAY: "FRI", SATURDAY: "SAT", SUNDAY: "SUN",
  // passthrough if already short
  MON: "MON", TUE: "TUE", WED: "WED", THU: "THU", FRI: "FRI", SAT: "SAT", SUN: "SUN",
};

const WindowAccordionHeader: React.FC<{ window: HourlyTariffWindow }> = ({ window }) => (
  <div className="flex align-items-center justify-content-between w-full gap-2">
    <span className="font-semibold text-700 text-sm white-space-nowrap">
      {window.startTime}–{window.endTime}
    </span>
    <div className="flex flex-wrap gap-1">
      {window.weekdays.map(d => (
        <span
          key={d}
          className="border-round px-2 py-1 text-xs font-medium"
          style={{ background: "var(--primary-100)", color: "var(--primary-700)" }}
        >
          {WEEKDAY_SHORT[d] ?? d}
        </span>
      ))}
    </div>
  </div>
);

const WindowAccordionContent: React.FC<{ window: HourlyTariffWindow }> = ({ window }) => (
  <div className="flex flex-column gap-3">
    <FeeSection icon="pi-bolt" iconColor="#f59e0b" title="Energy">
      <PriceLine label="Price per kWh" value={window.energyFee.price} />
      {window.activationFee.price !== "—" && (
        <PriceLine label="Activation fee" value={window.activationFee.price} />
      )}
    </FeeSection>
    <Divider className="my-0" />
    <FeeSection icon="pi-stop-circle" iconColor="#ef4444" title="Blocking Fee">
      <PriceLine label="Rate per hour" value={window.blockingFee.price} />
      {window.blockingFee.minDuration && (
        <PriceLine label="Starts after" value={window.blockingFee.minDuration} />
      )}
      <PriceLine label="Cap" value={window.blockingFeeCap.price} />
    </FeeSection>
  </div>
);

// ─── Hourly tariff body ───────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs font-semibold text-500 uppercase mb-2" style={{ letterSpacing: "0.05em" }}>
    {children}
  </div>
);

const HourlyTariffBody: React.FC<{ data: NonNullable<PriceHistoryRow["hourlyData"]> }> = ({ data }) => (
  <div className="flex flex-column gap-4">
    <div>
      <SectionLabel>Base Tariff</SectionLabel>
      <div className="flex flex-column gap-3 p-3 border-round" style={{ background: "var(--surface-50)" }}>
        <FeeSection icon="pi-bolt" iconColor="#f59e0b" title="Energy">
          <PriceLine label="Price per kWh" value={data.baseTariff.energyFee.price} />
          {data.baseTariff.activationFee.price !== "—" && (
            <PriceLine label="Activation fee" value={data.baseTariff.activationFee.price} />
          )}
        </FeeSection>
        <Divider className="my-0" />
        <FeeSection icon="pi-stop-circle" iconColor="#ef4444" title="Blocking Fee">
          <PriceLine label="Rate per hour" value={data.baseTariff.blockingFee.price} />
          {data.baseTariff.blockingFee.minDuration && (
            <PriceLine label="Starts after" value={data.baseTariff.blockingFee.minDuration} />
          )}
          <PriceLine label="Cap" value={data.baseTariff.blockingFeeCap.price} />
        </FeeSection>
      </div>
    </div>

    {data.tariffWindows.length > 0 && (
      <div>
        <SectionLabel>Tariff Windows ({data.tariffWindows.length})</SectionLabel>
        <Accordion multiple>
          {data.tariffWindows.map((w, i) => (
            <AccordionTab key={i} header={<WindowAccordionHeader window={w} />}>
              <WindowAccordionContent window={w} />
            </AccordionTab>
          ))}
        </Accordion>
      </div>
    )}
  </div>
);

// ─── Main card ────────────────────────────────────────────────────────────────

export const PriceHistoryCard: React.FC<PriceHistoryCardProps> = ({ item }) => {
  const isDayNight = item.tariff_type === "DAY_NIGHT";
  const isHourly = item.tariff_type === "HOURLY";

  return (
    <Card
      header={<CommunityPriceCardHeader tariffId={item.tariff_id} activeTill={item.active_till} tariffType={item.tariff_type} />}
      subTitle={<CommunityPriceCardDuration activeFrom={item.active_from} activeTill={item.active_till} />}
      className="mb-3"
    >
      {isHourly && item.hourlyData ? (
        <HourlyTariffBody data={item.hourlyData} />
      ) : isDayNight ? (
        <div className="flex flex-column gap-2">
          <PeriodBlock
            icon="pi-sun"
            iconColor="#f59e0b"
            label="Day"
            energyPrice={item.energy_price_day}
            blockingFee={item.blocking_fee_day}
            blockingFeeAfterHours={item.blocking_fee_after_hours_day}
            blockingFeeCap={item.blocking_fee_cap_day}
            blockingFeeCapAfterHours={item.blocking_fee_cap_after_hours_day}
          />
          <PeriodBlock
            icon="pi-moon"
            iconColor="#6366f1"
            label="Night"
            energyPrice={item.energy_price_night}
            blockingFee={item.blocking_fee_night}
            blockingFeeAfterHours={item.blocking_fee_after_hours_night}
            blockingFeeCap={item.blocking_fee_cap_night}
            blockingFeeCapAfterHours={item.blocking_fee_cap_after_hours_night}
          />
        </div>
      ) : (
        <StaticTariffBody
          energyPrice={item.energy_price}
          blockingFee={item.blocking_fee}
          blockingFeeAfterHours={item.blocking_fee_after_hours}
          blockingFeeCap={item.blocking_fee_cap}
          blockingFeeCapAfterHours={item.blocking_fee_cap_after_hours}
        />
      )}
    </Card>
  );
};
